import * as THREE from 'three';

const pendingRequests = {};

const loadCachedImage = ( url, onLoad, onProgress, onError ) => {
  let cached = THREE.Cache.get( url );

  // If fully cached and loaded, return immediately
  if ( cached !== undefined ) {
    if ( onLoad ) {
      setTimeout( () => {
        if ( onProgress ) onProgress( { loaded: 1, total: 1 } );
        onLoad( cached );
      }, 0 );
    }
    return cached;
  }

  // Check if we are already downloading this URL
  if ( pendingRequests[url] ) {
    // Attach to the existing request's promises
    pendingRequests[url].promise.then((image) => {
      onLoad && onLoad(image);
    }).catch((err) => {
      onError && onError(err);
    });
    
    // Add additional progress listener
    if (onProgress) {
      pendingRequests[url].progressListeners.push(onProgress);
    }
    return;
  }

  const request = new XMLHttpRequest();
  const urlCreator = window.URL || window.webkitURL;
  const image = document.createElementNS( 'http://www.w3.org/1999/xhtml', 'img' );
  image.crossOrigin = 'anonymous';

  // Setup the pending request object to broadcast events to all listeners
  pendingRequests[url] = {
    progressListeners: onProgress ? [onProgress] : [],
  };

  pendingRequests[url].promise = new Promise((resolve, reject) => {
    const onImageLoaded = () => {
      THREE.Cache.add( url, image );
      delete pendingRequests[url];
      resolve(image);
    };

    if ( url.indexOf( 'data:' ) === 0 ) {
      image.addEventListener( 'load', onImageLoaded, false );
      image.src = url;
      return;
    }

    request.open( 'GET', url, true );
    request.responseType = 'arraybuffer';
    
    request.onprogress = function ( event ) {
      if ( event.lengthComputable ) {
        pendingRequests[url].progressListeners.forEach(listener => {
          listener({ loaded: event.loaded, total: event.total });
        });
      }
    };

    request.onloadend = function() {
      if (this.status >= 200 && this.status < 300) {
        const arrayBufferView = new Uint8Array( this.response );
        const blob = new Blob( [ arrayBufferView ] );
        image.addEventListener( 'load', onImageLoaded, false );
        image.src = urlCreator.createObjectURL( blob );
      } else {
        delete pendingRequests[url];
        reject(new Error(`Failed to load image: ${this.status}`));
      }
    };

    request.onerror = function(err) {
      delete pendingRequests[url];
      reject(err);
    };

    request.send(null);
  });

  // Attach the final resolve to our onLoad callback
  pendingRequests[url].promise.then((img) => onLoad && onLoad(img)).catch((err) => onError && onError(err));
};

export default loadCachedImage;
